import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  BarChart, Bar,
} from 'recharts';
import { useTheme } from '@/themes/themes';

const API = '';

const VERDICT_COLORS = {
  SAFE: '#22c55e', SAFE_WITH_CAUTION: '#D4A017',
  CAUTION: '#f97316', CAUTION_SOFT: '#EAB308', CAUTION_HARD: '#f97316',
  DANGER: '#ef4444', UNKNOWN: '#8BA3C7',
};
const STATUS_COLORS = { ok: '#22c55e', error: '#ef4444', warning: '#EAB308', missing: '#6B829E' };
const CHART_COLORS = ['var(--havn-primary)', '#22c55e', '#EAB308', '#f97316', '#ef4444', '#8B5CF6', '#EC4899', '#6B829E'];
const RANGES = [
  { label: 'Today', value: 1 },
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: 'All time', value: 0 },
];
const TABS = ['Overview', 'Users', 'Database', 'Logs', 'Settings'];

// ═══ Styles ═══
const S = {
  page: { backgroundImage: 'url(/assets/havn_bg_dark.png)', backgroundSize: 'cover', backgroundPosition: 'center', minHeight: '100vh', color: '#E8EDF4', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', padding: '24px', maxWidth: 1400, margin: '0 auto' },
  card: { background: 'var(--havn-surface)', border: '1px solid var(--havn-border)', borderRadius: 12, padding: 20, marginBottom: 16 },
  sectionTitle: { fontSize: 14, fontWeight: 600, color: 'var(--havn-text-secondary, #8BA3C7)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12, marginTop: 24 },
  grid: (min) => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 12 }),
  metricCard: { background: 'var(--havn-surface)', border: '1px solid var(--havn-border)', borderRadius: 8, padding: 16, textAlign: 'center' },
  bigNum: (c = 'var(--havn-text)') => ({ fontSize: 36, fontWeight: 700, color: c }),
  label: { fontSize: 11, color: 'var(--havn-text-secondary, #8BA3C7)', marginTop: 4 },
  pill: (c) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, color: c, border: `1px solid ${c}30`, background: `${c}15` }),
  dot: (c) => ({ width: 8, height: 8, borderRadius: '50%', background: c, display: 'inline-block', marginRight: 8 }),
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { padding: '8px 12px', textAlign: 'left', color: 'var(--havn-text-secondary, #8BA3C7)', borderBottom: '1px solid var(--havn-border)', fontSize: 11, textTransform: 'uppercase' },
  td: { padding: '8px 12px', borderBottom: '1px solid var(--havn-border-subtle, rgba(30,58,95,0.15))' },
  btn: (active) => ({ background: active ? 'var(--havn-primary)' : 'var(--havn-border)', border: 'none', borderRadius: 8, padding: '6px 14px', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 12, transition: 'background 0.15s' }),
  btnDanger: { background: 'var(--havn-danger)', border: 'none', borderRadius: 8, padding: '6px 14px', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 12 },
  input: { background: 'var(--havn-bg)', border: '1px solid var(--havn-border)', borderRadius: 8, padding: '8px 12px', color: 'var(--havn-text)', fontSize: 13, width: '100%', boxSizing: 'border-box' },
  loginBox: { maxWidth: 380, margin: '120px auto', padding: 32, background: 'var(--havn-surface)', border: '1px solid var(--havn-border)', borderRadius: 16 },
  tabBar: { display: 'flex', gap: 4, marginBottom: 24, background: 'var(--havn-bg)', borderRadius: 10, padding: 4 },
  tab: (active) => ({ flex: 1, padding: '10px 0', textAlign: 'center', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: active ? 'var(--havn-border)' : 'transparent', color: active ? 'var(--havn-text)' : 'var(--havn-text-secondary, #8BA3C7)', border: 'none', transition: 'all 0.15s' }),
};
const tooltipStyle = { background: 'var(--havn-surface)', border: '1px solid var(--havn-border)', borderRadius: 8, color: 'var(--havn-text)' };

const Metric = ({ value, label, color }) => (
  <div style={S.metricCard}>
    <div style={S.bigNum(color)}>{value}</div>
    <div style={S.label}>{label}</div>
  </div>
);

const Section = ({ title, children }) => (<><div style={S.sectionTitle}>{title}</div>{children}</>);

const f = (url) => fetch(`${API}/api${url}`, { credentials: 'include' }).then(r => r.json()).catch(() => null);

// ═══════════════════════════════════════════════════════════════
// LOGIN PAGE
// ═══════════════════════════════════════════════════════════════
function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [firstRun, setFirstRun] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    f('/admin/check-first-run').then(d => d?.first_run && setFirstRun(true));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (data.ok) onLogin(data);
      else setError(data.error || 'Login failed');
    } catch { setError('Connection failed'); }
    setLoading(false);
  };

  return (
    <div style={S.page}>
      <div style={S.loginBox}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 24, fontWeight: 700 }}>HAVN Admin</div>
          <div style={{ fontSize: 12, color: '#8BA3C7', marginTop: 4 }}>Sign in to access the dashboard</div>
        </div>
        {firstRun && (
          <div style={{ background: 'var(--havn-primary-10)', border: '1px solid var(--havn-primary-20)', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12, color: 'var(--havn-primary)' }}>
            First time? Login with <strong>paul</strong> / <strong>havn2026</strong>
          </div>
        )}
        <form onSubmit={submit}>
          <input style={{ ...S.input, marginBottom: 12 }} placeholder="Username" value={username} onChange={e => setUsername(e.target.value)} autoFocus />
          <input style={{ ...S.input, marginBottom: 16 }} type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} />
          {error && <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 12 }}>{error}</div>}
          <button style={{ ...S.btn(true), width: '100%', padding: '12px 0', fontSize: 14 }} disabled={loading}>{loading ? 'Signing in...' : 'Sign In'}</button>
        </form>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CHANGE PASSWORD MODAL
// ═══════════════════════════════════════════════════════════════
function ChangePasswordModal({ forced, onDone }) {
  const [current, setCurrent] = useState('');
  const [newPass, setNewPass] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    const res = await fetch(`${API}/api/admin/change-password`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ current_password: current, new_password: newPass }),
    });
    const data = await res.json();
    if (data.ok) {
      setLoading(false);
      // Defer onDone to next tick to avoid React error #310
      // (setState during render transition when parent re-renders)
      setTimeout(() => onDone(), 0);
      return;
    }
    setError(data.error || 'Failed');
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000a', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ ...S.loginBox, margin: 0 }}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{forced ? 'Change Your Password' : 'Change Password'}</div>
        {forced && <div style={{ fontSize: 12, color: '#EAB308', marginBottom: 16 }}>You must change the default password before continuing.</div>}
        <form onSubmit={submit}>
          <input style={{ ...S.input, marginBottom: 12 }} type="password" placeholder="Current password" value={current} onChange={e => setCurrent(e.target.value)} autoFocus />
          <input style={{ ...S.input, marginBottom: 16 }} type="password" placeholder="New password (min 6 chars)" value={newPass} onChange={e => setNewPass(e.target.value)} />
          {error && <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 12 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...S.btn(true), flex: 1, padding: '10px 0' }} disabled={loading}>{loading ? 'Saving...' : 'Change Password'}</button>
            {!forced && <button type="button" style={{ ...S.btn(false), padding: '10px 16px' }} onClick={onDone}>Cancel</button>}
          </div>
        </form>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ═══════════════════════════════════════════════════════════════
function AdminDashboard() {
  const { theme, themeName, setTheme: setAppTheme, themeNames, themes } = useTheme();
  const [authed, setAuthed] = useState(null); // null=checking, false=not authed, {username}
  const [mustChangePass, setMustChangePass] = useState(false);
  const [showChangePass, setShowChangePass] = useState(false);
  const [tab, setTab] = useState('Overview');
  const [range, setRange] = useState(0); // Default to "All time"

  // Data state
  const [health, setHealth] = useState(null);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState(null);
  const [scans, setScans] = useState([]);
  const [errors, setErrors] = useState([]);
  const [recentErrors, setRecentErrors] = useState([]);
  const [verdicts, setVerdicts] = useState([]);
  const [domains, setDomains] = useState([]);
  const [scanTrend, setScanTrend] = useState([]);
  const [tierBreakdown, setTierBreakdown] = useState(null);
  const [latencyTrend, setLatencyTrend] = useState([]);
  const [safety, setSafety] = useState(null);
  const [chatMetrics, setChatMetrics] = useState(null);
  const [cacheStats, setCacheStats] = useState(null);

  // Users tab
  const [userList, setUserList] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userTotal, setUserTotal] = useState(0);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);

  // DB tab
  const [dbStats, setDbStats] = useState(null);
  const [clearConfirm, setClearConfirm] = useState('');
  const [clearTarget, setClearTarget] = useState('');
  const [cacheList, setCacheList] = useState([]);
  const [cacheDomainInput, setCacheDomainInput] = useState('');

  // Logs tab
  const [logs, setLogs] = useState([]);
  const [logLevel, setLogLevel] = useState('');
  const [logService, setLogService] = useState('');
  const [logSearch, setLogSearch] = useState('');
  const logEndRef = useRef(null);

  // Settings tab
  const [envStatus, setEnvStatus] = useState(null);
  const [settings, setSettings] = useState(null);

  // ── Auth check ──
  useEffect(() => {
    f('/admin/me').then(d => {
      if (d?.authenticated) setAuthed(d);
      else setAuthed(false);
    });
  }, []);

  const handleLogin = (data) => {
    setAuthed({ username: data.username });
    if (data.must_change_password) setMustChangePass(true);
  };

  const handleLogout = async () => {
    await fetch(`${API}/api/admin/logout`, { method: 'POST', credentials: 'include' });
    setAuthed(false);
  };

  const dp = range || '';

  // ── Fetch overview data ──
  const fetchOverview = useCallback(async () => {
    const [h, s, u, sc, e, re, v, d, st, tb, lt, sf, cm, cs] = await Promise.all([
      f('/health'), f(`/admin/stats?days=${range || 9999}`), f(`/admin/users${dp ? `?days=${dp}` : ''}`),
      f('/admin/scans?limit=30'), f('/admin/errors'), f('/admin/recent-errors?limit=30'),
      f(`/admin/verdicts?days=${range || 9999}`), f(`/admin/domains?days=${range || 9999}&limit=10`),
      f(`/admin/scan-trend?days=${range || 365}`), f(`/admin/tier-breakdown?days=${range || 9999}`),
      f(`/admin/latency-trend?days=${range || 365}`),
      f(`/admin/safety-metrics${dp ? `?days=${dp}` : ''}`),
      f(`/admin/chat-metrics${dp ? `?days=${dp}` : ''}`),
      f('/admin/cache-stats'),
    ]);
    setHealth(h); setStats(s); setUsers(u); setScans(sc || []); setErrors(e || []);
    setRecentErrors(re || []); setVerdicts(v || []); setDomains(d || []);
    setScanTrend(st || []); setTierBreakdown(tb); setLatencyTrend(lt || []);
    setSafety(sf); setChatMetrics(cm); setCacheStats(cs);
  }, [range, dp]);

  // ── Users tab state ──
  const [userSortBy, setUserSortBy] = useState('last_seen');
  const [userSortDir, setUserSortDir] = useState('desc');

  const handleUserSort = (col) => {
    if (userSortBy === col) {
      setUserSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setUserSortBy(col);
      setUserSortDir('desc');
    }
  };

  // ── Fetch tab-specific data ──
  const fetchUsers = useCallback(async () => {
    const d = await f(`/admin/users/list?search=${encodeURIComponent(userSearch)}&sort_by=${userSortBy}&sort_dir=${userSortDir}&limit=50`);
    if (d) { setUserList(d.users || []); setUserTotal(d.total || 0); }
  }, [userSearch, userSortBy, userSortDir]);

  const fetchDbStats = useCallback(async () => {
    const d = await f('/admin/db/stats');
    if (d) setDbStats(d);
  }, []);

  const fetchLogs = useCallback(async () => {
    const params = new URLSearchParams({ lines: '200' });
    if (logLevel) params.set('level', logLevel);
    if (logService) params.set('service', logService);
    if (logSearch) params.set('search', logSearch);
    const d = await f(`/admin/logs?${params}`);
    if (d) setLogs(d.lines || []);
  }, [logLevel, logService, logSearch]);

  const fetchSettings = useCallback(async () => {
    const [env, s] = await Promise.all([f('/admin/settings/env-status'), f('/admin/settings')]);
    if (env) setEnvStatus(env.env_status);
    if (s) setSettings(s);
  }, []);

  const fetchCacheList = useCallback(async () => {
    const res = await f('/admin/cache/list?limit=20');
    if (res) setCacheList(res.entries || []);
  }, []);

  // ── Load data on tab/range change ──
  useEffect(() => {
    if (!authed) return;
    if (tab === 'Overview') fetchOverview();
    if (tab === 'Users') fetchUsers();
    if (tab === 'Database') { fetchDbStats(); fetchCacheList(); }
    if (tab === 'Logs') fetchLogs();
    if (tab === 'Settings') fetchSettings();
  }, [tab, range, authed, fetchOverview, fetchUsers, fetchDbStats, fetchCacheList, fetchLogs, fetchSettings]);

  // ── Auto-refresh every 60s ──
  useEffect(() => {
    if (!authed) return;
    const iv = setInterval(() => {
      if (tab === 'Overview') fetchOverview();
      if (tab === 'Logs') fetchLogs();
    }, 60000);
    return () => clearInterval(iv);
  }, [tab, authed, fetchOverview, fetchLogs]);

  // ── Loading / Login states ──
  if (authed === null) return <div style={S.page}><div style={{ textAlign: 'center', marginTop: 200, color: '#8BA3C7' }}>Loading...</div></div>;
  if (authed === false) return <LoginPage onLogin={handleLogin} />;
  if (mustChangePass) return <ChangePasswordModal forced onDone={() => setMustChangePass(false)} />;

  const overallBadge = health?.status === 'healthy' ? { label: 'All Systems Go', color: '#22c55e' }
    : health?.status === 'degraded' ? { label: 'Degraded', color: '#EAB308' }
    : health?.status === 'critical' ? { label: 'Critical', color: '#ef4444' }
    : { label: '...', color: '#8BA3C7' };

  const pieData = (verdicts || []).filter(v => v.count > 0);
  const tierData = tierBreakdown ? [
    { name: 'Tier 1', value: tierBreakdown.tier1 },
    { name: 'Tier 2', value: tierBreakdown.tier2 },
    ...(tierBreakdown.unknown > 0 ? [{ name: 'Legacy', value: tierBreakdown.unknown }] : []),
  ].filter(d => d.value > 0) : [];
  const deviceData = users?.device_breakdown ? Object.entries(users.device_breakdown).map(([k, v]) => ({ name: k, value: v })) : [];

  // ── User detail expand ──
  const toggleUserDetail = async (uid) => {
    if (expandedUser === uid) { setExpandedUser(null); setUserDetail(null); return; }
    setExpandedUser(uid);
    const d = await f(`/admin/users/${encodeURIComponent(uid)}/detail`);
    setUserDetail(d);
  };

  const blockUser = async (uid, block) => {
    await fetch(`${API}/api/admin/users/${encodeURIComponent(uid)}/${block ? 'block' : 'unblock'}`, { method: 'POST', credentials: 'include' });
    fetchUsers();
  };

  // ── DB actions ──
  const exportCollection = (name) => {
    window.open(`${API}/api/admin/db/export/${name}`, '_blank');
  };
  const exportAll = () => {
    window.open(`${API}/api/admin/db/export-all`, '_blank');
  };
  const clearSingleCache = async (key) => {
    await fetch(`${API}/api/admin/cache/clear/${encodeURIComponent(key)}`, { method: 'POST', credentials: 'include' });
    fetchCacheList();
  };
  const clearCacheDomain = async () => {
    if (!cacheDomainInput.trim()) return;
    await fetch(`${API}/api/admin/cache/clear/${encodeURIComponent(cacheDomainInput.trim())}`, { method: 'POST', credentials: 'include' });
    setCacheDomainInput('');
    fetchCacheList();
  };
  const clearScanCache = async () => {
    if (!window.confirm('DANGER: Clear ALL cached scans? Every site will be rescanned from scratch on next visit. This is irreversible.')) return;
    await fetch(`${API}/api/admin/cache/clear`, { method: 'POST', credentials: 'include' });
    fetchDbStats();
    fetchCacheList();
  };
  const clearCollection = async (name) => {
    if (clearConfirm !== name) return;
    await fetch(`${API}/api/admin/db/clear/${name}`, { method: 'DELETE', credentials: 'include', headers: { 'X-Confirm-Collection': name } });
    setClearTarget(''); setClearConfirm('');
    fetchDbStats();
  };

  // ── Settings actions ──
  const updateSetting = async (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    await fetch(`${API}/api/admin/settings`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newSettings),
    });
  };

  return (
    <div style={S.page}>
      {showChangePass && <ChangePasswordModal onDone={() => setShowChangePass(false)} />}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 28, fontWeight: 700 }}>HAVN Admin</div>
          <div style={{ fontSize: 12, color: '#8BA3C7' }}>Logged in as <strong>{authed.username}</strong></div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {tab === 'Overview' && RANGES.map(r => (
            <button key={r.value} style={S.btn(range === r.value)} onClick={() => setRange(r.value)}>{r.label}</button>
          ))}
          <span style={S.pill(overallBadge.color)}>{overallBadge.label}</span>
          <button style={S.btn(false)} onClick={() => setShowChangePass(true)}>Password</button>
          <button style={{ ...S.btn(false), color: '#ef4444' }} onClick={handleLogout}>Logout</button>
        </div>
      </div>

      {/* Tab bar */}
      <div style={S.tabBar}>
        {TABS.map(t => <button key={t} style={S.tab(tab === t)} onClick={() => setTab(t)}>{t}</button>)}
      </div>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* OVERVIEW TAB */}
      {/* ═══════════════════════════════════════════════════════ */}
      {tab === 'Overview' && (
        <>
          {/* API Health */}
          <Section title="API Health">
            <div style={S.grid(180)}>
              {health?.services && Object.entries(health.services).map(([name, svc]) => (
                <div key={name} style={S.metricCard}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={S.dot(STATUS_COLORS[svc.status] || '#8BA3C7')} />
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#8BA3C7' }}>{svc.message}</div>
                </div>
              ))}
            </div>
          </Section>

          {/* User Metrics */}
          <Section title="Users">
            <div style={S.grid(160)}>
              <Metric value={users?.total_users || 0} label="Total Users" />
              <Metric value={users?.active_today || 0} label="Active Today" color="#22c55e" />
              <Metric value={users?.active_week || 0} label="This Week" />
              <Metric value={users?.active_month || 0} label="This Month" />
              <Metric value={users?.new_today || 0} label="New Today" color="var(--havn-primary)" />
            </div>
            {deviceData.length > 0 && (
              <div style={{ ...S.card, marginTop: 12 }}>
                <div style={{ fontSize: 12, color: '#8BA3C7', marginBottom: 8 }}>Device Breakdown</div>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart><Pie data={deviceData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                    {deviceData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i]} />)}
                  </Pie><Tooltip contentStyle={tooltipStyle} /></PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </Section>

          {/* Scan Metrics */}
          <Section title="Scans">
            <div style={S.grid(160)}>
              <Metric value={stats?.total_scans_7d || 0} label="Total Scans" />
              <Metric value={stats?.scans_today || 0} label="Today" color="var(--havn-primary)" />
              <Metric value={`${stats?.avg_latency_ms || 0}ms`} label="Avg Latency" />
              <Metric value={stats?.brand_upgrades_7d || 0} label="Brand Upgrades" />
            </div>
            {(scanTrend || []).length > 1 && (
              <div style={{ ...S.card, marginTop: 12 }}>
                <div style={{ fontSize: 12, color: '#8BA3C7', marginBottom: 8 }}>Scans Per Day</div>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={scanTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                    <XAxis dataKey="date" tick={{ fill: '#8BA3C7', fontSize: 10 }} tickFormatter={d => d?.slice(5)} />
                    <YAxis tick={{ fill: '#8BA3C7', fontSize: 10 }} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Line type="monotone" dataKey="count" stroke={theme.primary} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12 }}>
              {pieData.length > 0 && (
                <div style={{ ...S.card, flex: '1 1 300px' }}>
                  <div style={{ fontSize: 12, color: '#8BA3C7', marginBottom: 8 }}>Verdicts</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart><Pie data={pieData} dataKey="count" nameKey="verdict" cx="50%" cy="50%" outerRadius={70} label={({ verdict, count }) => `${verdict} (${count})`} labelLine={false} fontSize={10}>
                      {pieData.map((e, i) => <Cell key={i} fill={VERDICT_COLORS[e.verdict] || '#8BA3C7'} />)}
                    </Pie><Tooltip contentStyle={tooltipStyle} /></PieChart>
                  </ResponsiveContainer>
                </div>
              )}
              {tierData.length > 0 && (
                <div style={{ ...S.card, flex: '1 1 250px' }}>
                  <div style={{ fontSize: 12, color: '#8BA3C7', marginBottom: 8 }}>Tier 1 vs Tier 2</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart><Pie data={tierData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, value }) => `${name}: ${value}`} labelLine={false} fontSize={10}>
                      {tierData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i]} />)}
                    </Pie><Tooltip contentStyle={tooltipStyle} /></PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            {(domains || []).length > 0 && (
              <div style={{ ...S.card, marginTop: 12 }}>
                <div style={{ fontSize: 12, color: '#8BA3C7', marginBottom: 8 }}>Top Domains</div>
                <ResponsiveContainer width="100%" height={Math.max(200, domains.length * 28)}>
                  <BarChart data={domains} layout="vertical" margin={{ left: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                    <XAxis type="number" tick={{ fill: '#8BA3C7', fontSize: 10 }} />
                    <YAxis type="category" dataKey="hostname" tick={{ fill: '#E8EDF4', fontSize: 11 }} width={100} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="count" fill={theme.primary} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Section>

          {/* Scan Cache */}
          {cacheStats && (
            <Section title="Scan Cache">
              <div style={S.grid(160)}>
                <Metric value={cacheStats.total_cached_sites || 0} label="Cached Sites" />
                <Metric value={`${cacheStats.cache_hit_rate || 0}%`} label="Cache Hit Rate" color="#22c55e" />
                <Metric value={cacheStats.total_cache_hits || 0} label="Total Cache Hits" />
                <Metric value={`$${cacheStats.estimated_cost_saved || 0}`} label="Est. Cost Saved" color="var(--havn-primary)" />
              </div>
            </Section>
          )}

          {/* Chat */}
          <Section title="Chat & Voice">
            <div style={S.grid(180)}>
              <Metric value={chatMetrics?.total_conversations || 0} label="Conversations" />
              <Metric value={chatMetrics?.total_messages || 0} label="Messages" />
              <Metric value={chatMetrics?.avg_messages_per_session || 0} label="Avg Msgs/Session" />
            </div>
          </Section>

          {/* Safety */}
          <Section title="Safety Impact">
            <div style={S.grid(180)}>
              <Metric value={safety?.dangerous_sites || 0} label="Dangerous Sites" color="#ef4444" />
              <Metric value={safety?.scams_blocked || 0} label="Scams Blocked" color="#22c55e" />
              <Metric value={safety?.proceeded_anyway || 0} label="Proceeded Anyway" color="#EAB308" />
              <Metric value={safety?.caregiver_alerts || 0} label="Caregiver Alerts" color="var(--havn-primary)" />
              <Metric value={`$${(safety?.estimated_fraud_prevented || 0).toLocaleString()}`} label="Est. Fraud Prevented" color="#22c55e" />
            </div>
          </Section>

          {/* Errors */}
          <Section title="Errors">
            <Metric value={stats?.errors_24h || 0} label="Errors (24h)" color={stats?.errors_24h > 0 ? '#ef4444' : '#22c55e'} />
            {(errors || []).length > 0 && (
              <div style={{ ...S.card, marginTop: 12 }}>
                {errors.map((e, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--havn-border-subtle, rgba(30,58,95,0.15))' }}>
                    <div><strong>{e.service}</strong> <span style={{ color: '#8BA3C7' }}>— {e.error_type}</span></div>
                    <div style={{ color: '#ef4444', fontWeight: 600 }}>{e.count}x</div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Recent Scans Table */}
          <Section title="Recent Scans">
            <div style={S.card}>
              <table style={S.table}><thead><tr>
                <th style={S.th}>Time</th><th style={S.th}>Domain</th><th style={S.th}>Verdict</th>
                <th style={S.th}>Score</th><th style={S.th}>Tier</th><th style={S.th}>Latency</th>
              </tr></thead><tbody>
                {(scans || []).map((s, i) => (
                  <tr key={i}>
                    <td style={S.td}>{new Date(s.timestamp).toLocaleTimeString()}</td>
                    <td style={S.td}>{s.hostname}</td>
                    <td style={S.td}><span style={S.pill(VERDICT_COLORS[s.verdict] || '#8BA3C7')}>{s.verdict}</span></td>
                    <td style={S.td}>{s.score != null ? 100 - s.score : '—'}</td>
                    <td style={S.td}>{s.scan_type === 'tier1' ? 'T1' : s.scan_type === 'tier2' ? 'T2' : '—'}</td>
                    <td style={S.td}>{s.latency_ms}ms</td>
                  </tr>
                ))}
                {(scans || []).length === 0 && <tr><td style={{ ...S.td, color: '#6B829E', textAlign: 'center' }} colSpan={6}>No scans yet</td></tr>}
              </tbody></table>
            </div>
          </Section>
        </>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* USERS TAB */}
      {/* ═══════════════════════════════════════════════════════ */}
      {tab === 'Users' && (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <input style={{ ...S.input, maxWidth: 300 }} placeholder="Search users..." value={userSearch} onChange={e => { setUserSearch(e.target.value); }} onKeyDown={e => e.key === 'Enter' && fetchUsers()} />
            <button style={S.btn(true)} onClick={fetchUsers}>Search</button>
            <span style={{ color: '#8BA3C7', fontSize: 12, alignSelf: 'center' }}>{userTotal} users</span>
          </div>
          <div style={{ ...S.card, overflowX: 'auto' }}>
            {(() => {
              const SortTh = ({ col, children }) => (
                <th style={{ ...S.th, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleUserSort(col)}>
                  {children} {userSortBy === col ? (userSortDir === 'desc' ? '▾' : '▴') : ''}
                </th>
              );
              return (
            <table style={{ ...S.table, minWidth: '1100px' }}><thead><tr>
              <SortTh col="first_name">Name</SortTh>
              <SortTh col="email">Email</SortTh>
              <th style={S.th}>Today</th>
              <SortTh col="last_seen">Last Active</SortTh>
              <th style={S.th}>Week</th>
              <th style={S.th}>Month</th>
              <SortTh col="session_count">Sessions</SortTh>
              <SortTh col="scan_count">Scans</SortTh>
              <th style={S.th}>Status</th>
              <th style={S.th}>Actions</th>
            </tr></thead><tbody>
              {userList.map((u, i) => {
                const isTest = /^test[_-]|^TEST[_-]|test_flow|test_user/i.test(u._id || '');
                const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.display_name || '';
                return (
                <React.Fragment key={i}>
                  <tr style={{ cursor: 'pointer', opacity: isTest ? 0.6 : 1 }} onClick={() => toggleUserDetail(u._id)}>
                    <td style={S.td}>
                      <span style={{ fontSize: 12, fontWeight: name ? 600 : 400 }}>{name || <span style={{ color: '#6B829E', fontStyle: 'italic' }}>{(u._id || '').slice(0, 12)}...</span>}</span>
                      {isTest && <span style={{ ...S.pill('#f59e0b'), marginLeft: 6, fontSize: 9 }}>TEST</span>}
                    </td>
                    <td style={S.td}><span style={{ fontSize: 11 }}>{u.email || '—'}</span></td>
                    <td style={{ ...S.td, textAlign: 'center' }}>
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: u.active_today ? '#22c55e' : '#374151' }} title={u.active_today ? 'Active today' : 'Not active today'} />
                    </td>
                    <td style={S.td}>{u.last_seen ? new Date(u.last_seen).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}</td>
                    <td style={{ ...S.td, textAlign: 'center' }}><span style={{ fontSize: 11 }}>{u.days_this_week || 0}/7</span></td>
                    <td style={{ ...S.td, textAlign: 'center' }}><span style={{ fontSize: 11 }}>{u.days_this_month || 0}/30</span></td>
                    <td style={{ ...S.td, textAlign: 'center' }}>{u.session_count}</td>
                    <td style={{ ...S.td, textAlign: 'center' }}>{u.scan_count}</td>
                    <td style={S.td}>{u.blocked ? <span style={S.pill('#ef4444')}>Blocked</span> : <span style={S.pill('#22c55e')}>Active</span>}</td>
                    <td style={S.td}>
                      <button style={{ ...S.btn(false), fontSize: 11, padding: '4px 8px' }} onClick={(e) => { e.stopPropagation(); blockUser(u._id, !u.blocked); }}>
                        {u.blocked ? 'Unblock' : 'Block'}
                      </button>
                    </td>
                  </tr>
                  {expandedUser === u._id && userDetail && (
                    <tr><td colSpan={10} style={{ ...S.td, background: 'var(--havn-bg)', padding: 16 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, fontSize: 12, color: '#8BA3C7' }}>
                        <div>
                          <div style={{ fontWeight: 600, marginBottom: 6, color: '#E8EDF4' }}>Account</div>
                          <div>Name: {[userDetail.user?.first_name, userDetail.user?.last_name].filter(Boolean).join(' ') || userDetail.user?.display_name || '—'}</div>
                          <div>Email: {userDetail.user?.email || '—'}</div>
                          <div>Phone: {userDetail.user?.phone || '—'}</div>
                          <div>ID: <span style={{ fontFamily: 'monospace', fontSize: 10 }}>{u._id}</span></div>
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, marginBottom: 6, color: '#E8EDF4' }}>Profile</div>
                          <div>Age: {userDetail.user?.age_range || '—'}</div>
                          <div>Language: {userDetail.user?.language || '—'}</div>
                          <div>Referral: {userDetail.user?.referral_source || '—'}</div>
                          <div>Signed up: {userDetail.user?.created_at ? new Date(userDetail.user.created_at).toLocaleDateString() : '—'}</div>
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, marginBottom: 6, color: '#E8EDF4' }}>Device</div>
                          <div>Browser: {userDetail.user?.browser || '—'}</div>
                          <div>OS: {userDetail.user?.os || '—'}</div>
                          <div>Device: {userDetail.user?.device_type || '—'}</div>
                          <div>Platform: {userDetail.user?.platform || '—'}</div>
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, marginBottom: 6, color: '#E8EDF4' }}>Activity</div>
                          <div>Last active: {userDetail.user?.last_seen ? new Date(userDetail.user.last_seen).toLocaleString() : '—'}</div>
                          <div>Sessions: {userDetail.session_count ?? u.session_count}</div>
                          <div>Scans: {userDetail.scan_count ?? u.scan_count}</div>
                        </div>
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
                );
              })}
              {userList.length === 0 && <tr><td style={{ ...S.td, color: '#6B829E', textAlign: 'center' }} colSpan={10}>No users found</td></tr>}
            </tbody></table>
              );
            })()}
          </div>
        </>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* DATABASE TAB */}
      {/* ═══════════════════════════════════════════════════════ */}
      {tab === 'Database' && (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <button style={S.btn(true)} onClick={exportAll}>Export All (ZIP)</button>
            <button style={S.btn(false)} onClick={() => { fetchDbStats(); fetchCacheList(); }}>Refresh</button>
            {dbStats && <span style={{ color: 'var(--havn-text-secondary, #8BA3C7)', fontSize: 12, alignSelf: 'center' }}>
              DB Size: {((dbStats.total_size_bytes || 0) / 1024).toFixed(1)} KB | Storage: {((dbStats.storage_size_bytes || 0) / 1024).toFixed(1)} KB
            </span>}
          </div>

          {/* Scan Cache Management */}
          <div style={{ ...S.card, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--havn-text)', marginBottom: 12 }}>Scan Cache</div>

            {/* Per-site clear with autocomplete */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, position: 'relative' }}>
              <div style={{ position: 'relative', maxWidth: 320, flex: 1 }}>
                <input
                  style={{ ...S.input, width: '100%' }}
                  placeholder="Search cached domain..."
                  value={cacheDomainInput}
                  onChange={e => setCacheDomainInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && clearCacheDomain()}
                />
                {cacheDomainInput.length >= 3 && cacheList.filter(e => e.domain.includes(cacheDomainInput.toLowerCase())).length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--havn-surface)', border: '1px solid var(--havn-border)', borderRadius: 8, marginTop: 2, zIndex: 10, maxHeight: 160, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
                    {cacheList.filter(e => e.domain.includes(cacheDomainInput.toLowerCase())).map((e, i) => (
                      <div key={i} onClick={() => setCacheDomainInput(e.domain)}
                        style={{ padding: '8px 12px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid var(--havn-border-subtle, rgba(255,255,255,0.05))' }}
                        onMouseEnter={ev => ev.currentTarget.style.background = 'var(--havn-primary-10)'}
                        onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}>
                        <span style={{ fontFamily: 'monospace' }}>{e.domain}</span>
                        <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--havn-text-muted)' }}>{e.lang.toUpperCase()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button style={S.btn(false)} onClick={clearCacheDomain}>Clear Domain</button>
            </div>

            {/* Cache entries table */}
            {cacheList.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table style={S.table}><thead><tr>
                  <th style={S.th}>Domain</th><th style={S.th}>Lang</th>
                  <th style={S.th}>T1 Cached</th><th style={S.th}>T2 Cached</th>
                  <th style={S.th}>Scans</th><th style={S.th}>Ver</th><th style={S.th}></th>
                </tr></thead><tbody>
                  {cacheList.map((e, i) => (
                    <tr key={i}>
                      <td style={S.td}><span style={{ fontSize: 11, fontFamily: 'monospace' }}>{e.domain}</span></td>
                      <td style={S.td}><span style={{ fontSize: 11 }}>{e.lang.toUpperCase()}</span></td>
                      <td style={S.td}><span style={{ fontSize: 10 }}>{e.tier1_cached_at ? new Date(e.tier1_cached_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}</span></td>
                      <td style={S.td}><span style={{ fontSize: 10 }}>{e.tier2_cached_at ? new Date(e.tier2_cached_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}</span></td>
                      <td style={{ ...S.td, textAlign: 'center' }}>{e.scan_count}</td>
                      <td style={{ ...S.td, textAlign: 'center' }}>{e.version || '?'}</td>
                      <td style={S.td}>
                        <button style={{ ...S.btn(false), fontSize: 10, padding: '2px 8px', color: '#ef4444' }} onClick={() => clearSingleCache(e.key)}>Clear</button>
                      </td>
                    </tr>
                  ))}
                </tbody></table>
              </div>
            )}
            {cacheList.length === 0 && <div style={{ fontSize: 12, color: 'var(--havn-text-muted)', textAlign: 'center', padding: 12 }}>No cached scans</div>}

            {/* Clear All — danger zone */}
            <div style={{ marginTop: 16, padding: '12px 16px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.04)' }}>
              <div style={{ fontSize: 11, color: '#ef4444', fontWeight: 600, marginBottom: 6 }}>Danger Zone</div>
              <button style={{ ...S.btn(false), fontSize: 11, padding: '4px 12px', color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)' }} onClick={clearScanCache}>
                Clear All Cache
              </button>
              <span style={{ fontSize: 10, color: 'var(--havn-text-muted)', marginLeft: 8 }}>Forces all sites to rescan from scratch</span>
            </div>
          </div>
          <div style={S.card}>
            <table style={S.table}><thead><tr>
              <th style={S.th}>Collection</th><th style={S.th}>Documents</th><th style={S.th}>Actions</th>
            </tr></thead><tbody>
              {(dbStats?.collections || []).map((c, i) => (
                <tr key={i}>
                  <td style={S.td}><strong>{c.name}</strong></td>
                  <td style={S.td}>{c.document_count}</td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button style={{ ...S.btn(false), fontSize: 11, padding: '4px 8px' }} onClick={() => exportCollection(c.name)}>Export</button>
                      {c.name !== 'admins' && (
                        clearTarget === c.name ? (
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <input style={{ ...S.input, width: 120, fontSize: 11, padding: '4px 8px' }} placeholder={`Type "${c.name}"`} value={clearConfirm} onChange={e => setClearConfirm(e.target.value)} />
                            <button style={{ ...S.btnDanger, fontSize: 11, padding: '4px 8px' }} onClick={() => clearCollection(c.name)} disabled={clearConfirm !== c.name}>Clear</button>
                            <button style={{ ...S.btn(false), fontSize: 11, padding: '4px 8px' }} onClick={() => { setClearTarget(''); setClearConfirm(''); }}>Cancel</button>
                          </div>
                        ) : (
                          <button style={{ ...S.btnDanger, fontSize: 11, padding: '4px 8px', opacity: 0.6 }} onClick={() => setClearTarget(c.name)}>Clear</button>
                        )
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody></table>
          </div>
        </>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* LOGS TAB */}
      {/* ═══════════════════════════════════════════════════════ */}
      {tab === 'Logs' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <select style={{ ...S.input, width: 120 }} value={logLevel} onChange={e => setLogLevel(e.target.value)}>
              <option value="">All Levels</option>
              <option value="INFO">INFO</option>
              <option value="WARNING">WARNING</option>
              <option value="ERROR">ERROR</option>
            </select>
            <select style={{ ...S.input, width: 140 }} value={logService} onChange={e => setLogService(e.target.value)}>
              <option value="">All Services</option>
              {['FAST_SCAN', 'DEEP_SCAN', 'CHAT', 'TTS', 'HEALTH', 'SEARCH', 'ORCHESTRATOR', 'AUTH'].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <input style={{ ...S.input, maxWidth: 200 }} placeholder="Search logs..." value={logSearch} onChange={e => setLogSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchLogs()} />
            <button style={S.btn(true)} onClick={fetchLogs}>Refresh</button>
          </div>
          <div style={{ ...S.card, maxHeight: 600, overflowY: 'auto', fontFamily: 'monospace', fontSize: 11, lineHeight: 1.6 }}>
            {logs.length === 0 && <div style={{ color: '#6B829E', textAlign: 'center', padding: 40 }}>No log entries found</div>}
            {logs.map((line, i) => {
              const isErr = /ERROR/i.test(line);
              const isWarn = /WARNING/i.test(line);
              return (
                <div key={i} style={{ color: isErr ? '#ef4444' : isWarn ? '#EAB308' : '#8BA3C7', borderBottom: '1px solid var(--havn-border-subtle, rgba(30,58,95,0.1))', padding: '2px 0', wordBreak: 'break-all' }}>
                  {line}
                </div>
              );
            })}
            <div ref={logEndRef} />
          </div>
        </>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* SETTINGS TAB */}
      {/* ═══════════════════════════════════════════════════════ */}
      {tab === 'Settings' && (
        <>
          <Section title="Environment Variables">
            <div style={S.card}>
              <div style={S.grid(250)}>
                {envStatus && Object.entries(envStatus).map(([key, status]) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                    <span style={S.dot(status === 'set' ? '#22c55e' : '#ef4444')} />
                    <span style={{ fontSize: 12, fontFamily: 'monospace' }}>{key}</span>
                    <span style={{ ...S.pill(status === 'set' ? '#22c55e' : '#ef4444'), marginLeft: 'auto' }}>{status}</span>
                  </div>
                ))}
              </div>
            </div>
          </Section>

          <Section title="White-Label Theme">
            <div style={S.card}>
              <div style={{ fontSize: 12, color: '#8BA3C7', marginBottom: 12 }}>Select a theme to preview. Changes apply instantly across the app.</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {themeNames.map(name => {
                  const t = themes[name];
                  return (
                    <button key={name} onClick={() => setAppTheme(name)}
                      style={{
                        padding: '8px 14px', fontSize: 12, fontWeight: themeName === name ? 700 : 400,
                        background: themeName === name ? t.primary + '33' : 'rgba(255,255,255,0.04)',
                        border: `2px solid ${themeName === name ? t.primary : 'rgba(255,255,255,0.08)'}`,
                        borderRadius: 8, cursor: 'pointer', color: themeName === name ? t.primary : '#8BA3C7',
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}>
                      <span style={{ width: 12, height: 12, borderRadius: '50%', background: t.primary, flexShrink: 0 }} />
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </Section>

          <Section title="Voice Engine">
            <div style={S.card}>
              <div style={{ display: 'flex', gap: 12 }}>
                <button style={S.btn(settings?.voice_engine !== 'browser')} onClick={() => updateSetting('voice_engine', 'elevenlabs')}>ElevenLabs</button>
                <button style={S.btn(settings?.voice_engine === 'browser')} onClick={() => updateSetting('voice_engine', 'browser')}>Browser (Free)</button>
              </div>
              <div style={{ fontSize: 11, color: '#8BA3C7', marginTop: 8 }}>
                {settings?.voice_engine === 'browser' ? 'Using browser built-in speech synthesis (no API cost)' : 'Using ElevenLabs TTS (requires valid API key)'}
              </div>
            </div>
          </Section>

          <Section title="Maintenance Mode">
            <div style={S.card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  style={settings?.maintenance_mode ? S.btnDanger : S.btn(false)}
                  onClick={() => {
                    if (!settings?.maintenance_mode || window.confirm('This will show a maintenance page to all users. Continue?')) {
                      updateSetting('maintenance_mode', !settings?.maintenance_mode);
                    }
                  }}
                >
                  {settings?.maintenance_mode ? 'Maintenance ON — Click to Disable' : 'Enable Maintenance Mode'}
                </button>
                {settings?.maintenance_mode && <span style={S.pill('#ef4444')}>ACTIVE — Users see maintenance page</span>}
              </div>
            </div>
          </Section>

          <Section title="Account">
            <div style={S.card}>
              <button style={S.btn(false)} onClick={() => setShowChangePass(true)}>Change Password</button>
            </div>
          </Section>
        </>
      )}

      <div style={{ textAlign: 'center', color: '#6B829E', fontSize: 11, marginTop: 32, padding: 16 }}>
        HAVN Admin v5.0 — Auto-refreshes every 60s
      </div>
    </div>
  );
}

export default AdminDashboard;
